// Utility functions for CompensationPlan dropdown, index, and income input logic

/**
 * Returns the last index in the ages array that is filled (not null/undefined)
 */
export function getLastFilledAgeIndex(ages: (number | null)[]): number {
  let lastFilled = -1;
  for (let i = 0; i < ages.length; i++) {
    if (ages[i] !== null && ages[i] !== undefined) {
      lastFilled = i;
    }
  }
  return lastFilled;
}

/**
 * Disables age dropdowns unless all previous are filled
 */
export function isAgeDropdownDisabled(idx: number, ages: (number | null)[]): boolean {
  for (let i = 0; i <= idx; i++) {
    if (ages[i] === null || ages[i] === undefined) return true;
  }
  return false;
}

/**
 * Validates the interval calculation cells for the pension policy form.
 * Returns createError with a message if any invalid cell ("red border"), or if none are fully valid.
 * Otherwise returns true.
 */
export function validateIntervalCalculationCells(items: any[], createError: (arg: { message: string }) => any) {
  let hasValid = false;
  let hasRed = false;

  for (const item of items) {
    const errors = getCellValidationErrors(item);
    if (!errors) {
      if (
        item.value !== null &&
        item.value !== undefined &&
        item.value !== '' &&
        item.age?.min !== null &&
        item.age?.min !== undefined &&
        item.age?.min !== '' &&
        item.age?.max !== null &&
        item.age?.max !== undefined &&
        item.age?.max !== '' &&
        item.indexAmount?.min !== null &&
        item.indexAmount?.min !== undefined &&
        item.indexAmount?.min !== '' &&
        item.indexAmount?.max !== null &&
        item.indexAmount?.max !== undefined &&
        item.indexAmount?.max !== ''
      ) {
        hasValid = true;
      }
    } else {
      hasRed = true;
      break;
    }
  }

  if (hasRed) {
    return createError({ message: 'Please fill all required fields for each interval (no red borders).' });
  }
  if (!hasValid) {
    return createError({ message: 'At least one valid group/interval is required.' });
  }
  return true;
}

/**
 * Disables amount index inputs unless all previous are filled
 */
export function isAmountIndexDisabled(idx: number, amounts: (number | null)[]): boolean {
  for (let i = 0; i <= idx; i++) {
    if (amounts[i] === null || amounts[i] === undefined) return true;
  }
  return false;
}

/**
 * Determines if the "select..." option should be disabled for a given age dropdown
 */
export function isSelectOptionDisabled(idx: number, ages: (number | null)[]): boolean {
  const lastFilled = getLastFilledAgeIndex(ages);
  if (idx !== lastFilled) return true;
  for (let i = idx + 1; i < ages.length; i++) {
    if (ages[i] !== null && ages[i] !== undefined) return true;
  }
  return false;
}

/**
 * Disables income input unless both age and index intervals are filled
 */
export function isIncomeInputDisabled(
  groupIdx: number,
  levelIdx: number,
  ageCollection: (number | null)[],
  amountIndexCollection: (number | null)[],
): boolean {
  const isEmpty = (v: any) => v === null || v === undefined || v === '';
  return (
    isEmpty(ageCollection[groupIdx]) ||
    isEmpty(ageCollection[groupIdx + 1]) ||
    isEmpty(amountIndexCollection[levelIdx]) ||
    isEmpty(amountIndexCollection[levelIdx + 1])
  );
}

/**
 * Returns an array of missing fields or null if the cell is valid.
 */
export function getCellValidationErrors(item: any): string[] | null {
  const isEmpty = (v: any) => v === null || v === undefined || v === '';
  const valueFilled = !isEmpty(item.value);
  const ageMinFilled = !isEmpty(item.age?.min);
  const ageMaxFilled = !isEmpty(item.age?.max);
  const idxMinFilled = !isEmpty(item.indexAmount?.min);
  const idxMaxFilled = !isEmpty(item.indexAmount?.max);

  // All fully filled
  if (valueFilled && ageMinFilled && ageMaxFilled && idxMinFilled && idxMaxFilled) return null; // valid

  // All empty
  if (!valueFilled && !ageMinFilled && !ageMaxFilled && !idxMinFilled && !idxMaxFilled) return null; // valid

  // If all intervals are filled but value is empty, treat as invalid
  if (!valueFilled && ageMinFilled && ageMaxFilled && idxMinFilled && idxMaxFilled) {
    return ['value'];
  }

  // Partial allowed only if value is null
  if (!valueFilled) return null; // valid

  // If value is set, but any interval is missing, it's invalid
  const missingFields: string[] = [];
  if (!ageMinFilled) missingFields.push('age.min');
  if (!ageMaxFilled) missingFields.push('age.max');
  if (!idxMinFilled) missingFields.push('indexAmount.min');
  if (!idxMaxFilled) missingFields.push('indexAmount.max');
  return missingFields.length > 0 ? missingFields : null;
}
